import React, { useEffect, useState } from "react";
import { DatePickerInput, Label } from "../../../components/ui";
import { CreateEventService, checkHashTagService } from "../../../apis/events";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Button, Form, Input, Alert, Switch } from "antd";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { tostE, tostS } from "../../../config/toastConfig";

export default function CreateEvent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const originalEventName = searchParams.get("event_name") || "";
  const expectedHashtag = originalEventName.replace(/[^a-zA-Z0-9]/g, "");

  const [hashAvailable, setHashAvailable] = useState(null);
  const [hashTerm, setHashTerm] = useState(expectedHashtag);
  const [checking, setChecking] = useState(false);

  const CreateEventSchema = Yup.object().shape({
    event_name: Yup.string().required("Event name is required"),
    hastag: Yup.string().required("Hashtag is required"),
    start_date: Yup.date().required("Start date is required"),
    end_date: Yup.date().required("End date is required"),
  });

  const formik = useFormik({
    initialValues: {
      event_name: originalEventName,
      hastag: expectedHashtag,
      start_date: "",
      end_date: "",
      show_event_name: false,
    },
    validationSchema: CreateEventSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      if (!hashAvailable) return;

      const body = {
        event_title: values.event_name,
        hastag: values.hastag,
        start_date: values.start_date.format("YYYY-MM-DD"),
        end_date: values.end_date.format("YYYY-MM-DD"),
        source: "auto_register",
        show_event_name: values.show_event_name ? 1 : 0,
      };

      try {
        const res = await CreateEventService(body);
        if (res.status === 200 && res.data.success) {
          tostS(res.data.message);
          const isAutoLogin = !!originalEventName || res.data.event_id;
          if (isAutoLogin && res.data.event_id) {
            navigate(`/app/events/manage/${res.data.event_id}/overview`, { replace: true });
          } else {
            navigate("/app/events", { replace: true });
          }
        } else {
          if (res.data.message && typeof res.data.message === "object") {
            const fieldErrors = {};
            Object.keys(res.data.message).forEach((key) => {
              fieldErrors[key] = res.data.message[key][0];
            });
            setErrors(fieldErrors);
          }
          tostE("Please fix the errors in the form.");
        }
      } catch (error) {
        tostE(error.response?.data?.message || "An error occurred.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, setValues, errors, touched, getFieldProps, setFieldValue, handleSubmit, isSubmitting } = formik;

  useEffect(() => {
    if (!hashTerm) return;
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await checkHashTagService({ hastag: hashTerm });
        setHashAvailable(res.status === 200 && res.data.success);
        setFieldValue("hastag", hashTerm);
      } catch {
        // ignore
      } finally {
        setChecking(false);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [hashTerm]); // eslint-disable-line

  useEffect(() => {
    if (!expectedHashtag) return;
    checkHashTagService({ hastag: expectedHashtag })
      .then((res) => setHashAvailable(res.status === 200 && res.data.success))
      .catch(() => {});
  }, []); // eslint-disable-line

  return (
    <div style={{ display: "flex", justifyContent: "center"}}>
      <div style={{ width: "100%", maxWidth: 560, background: "#fff", borderRadius: 12, padding: "32px 32px 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
        <h3 style={{ margin: "0 0 20px", fontSize: 20, fontWeight: 700 }}>Create Event</h3>

        {originalEventName && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
            message={`Event "${originalEventName}" is not registered with us. Please fill the form below to create it.`}
          />
        )}

        <FormikProvider value={formik}>
          <Form layout="vertical" onFinish={handleSubmit}>
            <Label
              label="Event Name"
              error={touched.event_name && errors.event_name}
              helperText={touched.event_name && errors.event_name ? errors.event_name : ""}
              required
            >
              <Input
                placeholder="Enter event name"
                {...getFieldProps("event_name")}
                onChange={(e) => {
                  const name = e.target.value;
                  setFieldValue("event_name", name);
                  const derived = name.replace(/[^a-zA-Z0-9]/g, "");
                  setHashTerm(derived);
                  setFieldValue("hastag", derived);
                  setHashAvailable(null);
                }}
              />
            </Label>

            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "-8px 0 16px" }}>
              <Switch
                size="small"
                checked={values.show_event_name}
                onChange={(checked) => setFieldValue("show_event_name", checked)}
              />
              <span style={{ fontSize: 13 }}>Show event name on web page?</span>
            </div>

            <Label
              label="Hashtag"
              error={touched.hastag && errors.hastag}
              helperText={touched.hastag && errors.hastag ? errors.hastag : ""}
              required
            >
              <Input
                autoComplete="off"
                addonBefore="#"
                placeholder="Enter hashtag"
                value={hashTerm}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                  setHashTerm(val);
                  setFieldValue("hastag", val);
                  setHashAvailable(null);
                }}
              />
              {checking && <p style={{ color: "#888", margin: "4px 0 0" }}>Checking...</p>}
              {hashTerm !== "" && !checking && hashAvailable !== null && (
                hashAvailable
                  ? <p className="text-success">You made a great choice</p>
                  : <p className="text-danger">Sorry! This hashtag is already taken</p>
              )}
            </Label>

            <Label
              label="Start Date"
              error={touched.start_date && errors.start_date}
              helperText={touched.start_date && errors.start_date ? errors.start_date : ""}
              required
            >
              <DatePickerInput
                placeholder="Select Start Date"
                showNow={false}
                value={values.start_date}
                onChange={(date) => {
                  setValues((prev) => ({
                    ...prev,
                    start_date: date,
                    end_date: dayjs(date).add(30, "day"),
                  }));
                }}
                minDate={dayjs(new Date())}
                fullWidth
              />
            </Label>

            <Label
              label="End Date"
              error={touched.end_date && errors.end_date}
              helperText={touched.end_date && errors.end_date ? errors.end_date : ""}
              required
            >
              <DatePickerInput
                placeholder="Select End Date"
                showNow={false}
                value={values.end_date || (values.start_date ? dayjs(values.start_date).add(30, "day") : "")}
                onChange={() => {}}
                minDate={dayjs(values.start_date)}
                fullWidth
                disabled
              />
            </Label>

            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={!hashAvailable || checking}
              style={{ width: "100%", marginTop: 8 }}
            >
              Create Event
            </Button>
          </Form>
        </FormikProvider>
      </div>
    </div>
  );
}
